import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { EmployeeModel } from '../models/EmployeeModel';
import { LoggingService } from '../shared/services/logging';
import * as fromDataServices from '../data-services';
import moment from 'moment';
import { IGenericResponse } from '../shared';

@Injectable({
    providedIn: 'root'
})
export class QuickbooksService {
    private uri = `${environment.apiLocation}/QuickbooksOnline`;
    private employeeUri = `${environment.apiLocation}/Employee`;
    constructor(
        private http: HttpClient,
        protected log: LoggingService,
        private store: Store
    ) {}
    public async Connect(code: string, state: string, realmId: string) {
        this.log.verbose({
            type: 'QuickbooksService:Connect',
            code,
            state,
            realmId
        });
        return this.http
            .get<any>(
                `${this.uri}/Callback?code=${code}&state=${state}&realmId=${realmId}`
            )
            .toPromise();
    }
    public async SyncTransactions(
        ProcessScheduleID_List: string,
        SyncGLOnly = false
    ) {
        this.log.verbose({ type: 'QuickbooksService:SyncTransactions' });
        return this.http
            .post(`${this.uri}/SyncTransactions`, {
                ProcessScheduleIds: ProcessScheduleID_List,
                SyncGLOnly
            })
            .toPromise();
    }
    public async SyncCompanyInfo() {
        this.log.verbose({ type: 'QuickbooksService:SyncCompanyInfo' });
        return this.http.get(`${this.uri}/SyncCompanyInfo`).toPromise();
    }
    public async SyncCustomers() {
        this.log.verbose({ type: 'QuickbooksService:SyncCustomers' });
        return this.http.get(`${this.uri}/SyncCustomers`).toPromise();
    }
    public async SyncAccounts() {
        this.log.verbose({ type: 'QuickbooksService:SyncAccounts' });
        return this.http.get(`${this.uri}/SyncAccounts`).toPromise();
    }
    public async SyncEmployees() {
        this.log.verbose({ type: 'QuickbooksService:SyncEmployees' });
        return this.http.get(`${this.uri}/SyncEmployees`).toPromise();
    }
    public async SyncVendors() {
        this.log.verbose({ type: 'QuickbooksService:SyncVendors' });
        return this.http.get(`${this.uri}/SyncVendors`).toPromise();
    }
    public async SyncServiceItems() {
        this.log.verbose({ type: 'QuickbooksService:SyncServiceItems' });
        return this.http.get(`${this.uri}/SyncServiceItems`).toPromise();
    }
    public SyncInventory() {
        this.log.verbose({ type: 'QuickbooksService:SyncInventory' });
        return this.http.get(`${this.uri}/SyncInventory`).toPromise();
    }
    public async SyncNonInventory() {
        this.log.verbose({ type: 'QuickbooksService:SyncNonInventory' });
        return this.http.get(`${this.uri}/SyncNonInventory`).toPromise();
    }
    public async SyncOtherItems() {
        this.log.verbose({ type: 'QuickbooksService:SyncOtherItems' });
        return this.http.get(`${this.uri}/SyncOtherItems`).toPromise();
    }
    public async SyncClasses() {
        this.log.verbose({ type: 'QuickbooksService:SyncClasses' });
        return this.http.get(`${this.uri}/SyncClasses`).toPromise();
    }
    public SyncCustomerJobs() {
        this.log.verbose({ type: 'QuickbooksService:SyncCustomerJobs' });
        return this.http.get(`${this.uri}/SyncCustomerJobs`).toPromise();
    }
    public async SyncPreferences() {
        this.log.verbose({ type: 'QuickbooksService:SyncPreferences' });
        return this.http.get(`${this.uri}/SyncPreferences`).toPromise();
    }
    public HasValidTokens(): Observable<boolean> {
        this.log.verbose({ type: 'QuickbooksService:HasValidTokens' });
        return this.http.get<boolean>(`${this.uri}/HasValidTokens`);
    }
    public GetAuthUrl(): Observable<string> {
        this.log.verbose({ type: 'QuickbooksService:GetAuthUrl' });
        return this.http.get<string>(`${this.uri}/GetAuthUrl`);
    }
    public GetEmployeesWithHours(
        startDate: Date,
        endDate: Date,
        resend = false
    ): Observable<EmployeeModel[]> {
        this.log.verbose({
            type: 'QuickbooksService:GetEmployeesWithTime',
            startDate,
            endDate
        });
        const params = {
            StartDate: startDate
                ? moment(startDate).format('YYYY-MM-DD')
                : (null as any),
            EndDate: endDate
                ? moment(endDate).format('YYYY-MM-DD')
                : (null as any),
            Resend: resend
        };
        return this.http.post<EmployeeModel[]>(
            `${this.employeeUri}/GetEmployeesWithTime`,
            params
        );
    }
    public GetContractorsWithHours(
        startDate: Date,
        endDate: Date,
        resend = false
    ): Observable<EmployeeModel[]> {
        this.log.verbose({ type: 'QuickbooksService:GetContractorsWithTime' });
        const params = {
            StartDate: startDate
                ? moment(startDate).format('YYYY-MM-DD')
                : (null as any),
            EndDate: endDate
                ? moment(endDate).format('YYYY-MM-DD')
                : (null as any),
            Resend: resend
        };
        return this.http.post<EmployeeModel[]>(
            `${this.employeeUri}/GetContractorsWithTime`,
            params
        );
    }
    public GetEmployeesWithPurchases(
        startDate: Date,
        endDate: Date,
        resend = false
    ) {
        this.log.verbose({
            type: 'QuickbooksService:GetEmployeesWithPurchases',
            startDate,
            endDate,
            resend
        });
        const params = {
            StartDate: startDate
                ? moment(startDate).format('YYYY-MM-DD')
                : null,
            EndDate: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
            Resend: resend
        };
        return this.http.post<EmployeeModel[]>(
            `${this.employeeUri}/GetEmployeesWithPurchases`,
            params
        );
    }
    public GetMinApprovedTimeNotSent() {
        return this.http.get<Date>(`${this.uri}/GetMinApprovedTimeNotSent`);
    }
    public GetMaxApprovedTimeNotSent() {
        return this.http.get<Date>(`${this.uri}/GetMaxApprovedTimeNotSent`);
    }
    public SendHours(
        EmployeeListID: string,
        startDate: Date,
        endDate: Date,
        resend: boolean
    ) {
        this.log.verbose({ type: 'QuickbooksService:SendHours' });
        return this.http.post(`${this.uri}/SendTimeActivity`, {
            EmployeeListID,
            StartDate: startDate,
            EndDate: endDate,
            Resend: resend
        });
    }
    public SendJournalEntries(filter: {
        EndDate: Date;
        ReversalDate: Date;
        JournalType: string;
    }): Observable<unknown> {
        this.log.verbose({
            type: 'QuickbooksService:SendJournalEntries',
            filter
        });
        return this.http.post(`${this.uri}/SendJournalEntries`, filter);
    }
    public SendExpenses(
        EmployeeListID: string,
        startDate: Date,
        endDate: Date,
        Resend: boolean,
        sendImages: boolean
    ) {
        this.log.verbose({ type: 'QuickbooksService:SendExpenses' });
        return this.http.post(`${this.uri}/SendExpenses`, {
            EmployeeListID,
            StartDate: startDate,
            EndDate: endDate,
            Resend: Resend,
            SendImages: sendImages
        });
    }
    public SendInvoices(
        items: fromDataServices.Invoice.InvoiceModel[]
    ): Observable<IGenericResponse<fromDataServices.Invoice.InvoiceModel>> {
        this.log.verbose({ type: 'QuickbooksService:SendInvoices' });
        return this.http.post<
            IGenericResponse<fromDataServices.Invoice.InvoiceModel>
        >(
            `${this.uri}/SendInvoices`,
            items.map(x => ({
                ...x,
                CustJobListID: x.listid
            }))
        );
    }
    public async SendEmployees() {
        this.log.verbose({ type: 'QuickbooksService:SendEmployees' });
        return this.http.get(`${this.uri}/SendEmployees`).toPromise();
    }
    public RevokeAccess(): Observable<any> {
        this.log.verbose({ type: 'QuickbooksService:RevokeAccess' });
        return this.http.get(`${this.uri}/RevokeAccess`);
    }
}
